import React from 'react';
import PropTypes from 'prop-types';
import { Building2, LockKeyhole, Plus } from 'lucide-react';
import { Pie, PieChart } from 'recharts';
import { getUserName } from 'helpers';
import { useGetUser } from 'hooks';
import {
  CATEGORY_NON_RESIDENT,
  CATEGORY_NON_RESIDENT_PREMIUM,
  CATEGORY_NON_RESIDENT_STANDARD,
  CATEGORY_NON_RESIDENT_TOTAL_SEATS,
} from 'data';
import StandaloneAccountMenu from 'components/shared/StandaloneAccountMenu';
import { LockableButton } from 'components/shared/Lockable';
import { ChartContainer } from 'components/ui/chart';

const Header = ({
  agency = null,
  categories = null,
  onOpenAgencyModal = () => {},
  onOpenAddPilgrimModal = () => {},
}) => {
  const { currentUserFirstname, currentUserLastname } = useGetUser();
  const diasporaStandard = categories?.find((category) => category.name.includes(CATEGORY_NON_RESIDENT_STANDARD));
  const diasporaPremium = categories?.find((category) => category.name.includes(CATEGORY_NON_RESIDENT_PREMIUM));
  const groupedDiaspora = {
    id: CATEGORY_NON_RESIDENT,
    name: CATEGORY_NON_RESIDENT,
    max_seats: CATEGORY_NON_RESIDENT_TOTAL_SEATS,
    occupied_seats_standard: diasporaStandard?.occupied_seats || 0,
    occupied_seats_premium: diasporaPremium?.occupied_seats || 0,
  };
  const displayedCategories = categories
    ? [...categories.filter((category) => !category.name.includes(CATEGORY_NON_RESIDENT)), groupedDiaspora]
    : [];

  return (
    <header className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex min-w-0 items-start gap-3">
          <StandaloneAccountMenu compact />
          <div className="min-w-0 pt-0.5">
            <h1 className="text-[22px] font-semibold leading-7 tracking-[-0.35px] text-[#203329] sm:text-2xl">
              Bienvenue,{' '}
              <span className="text-hajj-primary">{getUserName(currentUserFirstname, currentUserLastname)}</span>
            </h1>
            <p className="mt-1 text-sm leading-5 text-[#657269]">
              Gérez vos pèlerins, suivez leurs démarches et effectuez vos paiements.
            </p>
          </div>
        </div>
        <LockableButton
          variant="hajj"
          disabled={!agency}
          onClick={onOpenAddPilgrimModal}
          className="w-full shadow-sm sm:w-auto"
        >
          <Plus className="size-4" /> Ajouter un pèlerin
        </LockableButton>
      </div>

      {!agency ? (
        <section className="flex flex-col gap-4 rounded-[10px] border border-[#c9dfd1] bg-[#eef7f1] p-4 sm:flex-row sm:items-center">
          <span className="grid size-9 shrink-0 place-items-center rounded-md border border-[#cfe0d5] bg-white text-hajj-primary">
            <Building2 className="size-5" aria-hidden="true" />
          </span>
          <div className="min-w-0 flex-1">
            <h2 className="text-sm font-semibold text-[#264232]">Société agréée</h2>
            <p className="mt-0.5 text-sm text-[#55675d]">
              Choisissez une société agréée pour commencer et ajouter vos pèlerins.
            </p>
          </div>
          <LockableButton variant="hajj" onClick={onOpenAgencyModal} className="w-full sm:w-auto">
            Sélectionner une société agréée
          </LockableButton>
        </section>
      ) : (
        <section className="flex flex-col gap-3 border-b border-[#dce4df] pb-5 sm:flex-row sm:items-center">
          <span className="grid size-9 shrink-0 place-items-center rounded-md border border-[#d7e3db] bg-white text-[#4b735c]">
            <Building2 className="size-5" aria-hidden="true" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-xs text-[#68776e]">Société agréée</p>
            <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
              <h2 className="text-sm font-semibold text-[#284333]">{agency.name}</h2>
              <p className="text-sm text-[#657269]">Tous vos pèlerins sont rattachés à cette société.</p>
            </div>
          </div>
          <span className="inline-flex items-center gap-1.5 self-start rounded-md bg-[#edf2ef] px-2.5 py-1.5 text-xs text-[#657269] sm:self-center">
            <LockKeyhole className="size-3.5" aria-hidden="true" /> Société du compte
          </span>
        </section>
      )}

      {displayedCategories.length > 0 && (
        <section aria-labelledby="hajj-availability-heading">
          <h2 id="hajj-availability-heading" className="mb-2 text-sm font-medium text-[#53675a]">
            Disponibilité du Hajj
          </h2>
          <div className="grid overflow-hidden rounded-[10px] border border-[#d7e3db] bg-[#edf4ef] sm:grid-cols-2 lg:grid-cols-3">
            {displayedCategories.map((category) => {
              const isDiaspora = category.name.includes(CATEGORY_NON_RESIDENT);
              const occupied = isDiaspora
                ? category.occupied_seats_standard + category.occupied_seats_premium
                : category.occupied_seats;
              const available = category.max_seats - occupied;
              const chartData = [
                { name: 'Confirmés', value: occupied, fill: '#78ad90' },
                { name: 'Disponibles', value: Math.max(available, 0), fill: '#ffffff' },
              ];

              return (
                <article
                  key={category.id}
                  className="relative min-h-[116px] border-b border-[#d7e3db] px-5 py-4 last:border-b-0 sm:border-r sm:[&:nth-child(2n)]:border-r-0 lg:border-b-0 lg:[&:nth-child(2n)]:border-r lg:last:border-r-0"
                >
                  <h3 className="text-sm font-semibold text-[#30483a]">{category.name}</h3>
                  <p className="mt-1 flex items-baseline gap-1.5 text-[#557064]">
                    <strong className="text-[27px] font-semibold leading-8 tabular-nums text-[#195f39]">
                      {available}
                    </strong>
                    <span className="text-xs">places disponibles</span>
                  </p>
                  <p className="mt-1 text-xs text-[#78857d]">{occupied} pèlerins confirmés</p>
                  <ChartContainer config={{}} className="absolute right-4 top-4 size-7">
                    <PieChart>
                      <Pie data={chartData} dataKey="value" innerRadius={8} outerRadius={12} strokeWidth={0} />
                    </PieChart>
                  </ChartContainer>
                </article>
              );
            })}
          </div>
        </section>
      )}
    </header>
  );
};

Header.propTypes = {
  agency: PropTypes.shape({ name: PropTypes.string }),
  categories: PropTypes.arrayOf(PropTypes.shape({})),
  onOpenAddPilgrimModal: PropTypes.func,
  onOpenAgencyModal: PropTypes.func,
};

export default Header;
